/**
 * Locale bundles available under `src/js/i18n/<language>/default.json`.
 * Entries must match the directory casing exactly: dynamic imports resolve
 * them as literal paths (webpack context keys / Vite glob keys), so a
 * lowercase mismatch like `en-gb` fails silently on case-sensitive builds.
 *
 * Regional entries (`en-GB`, `pl-PL`, `pt-PT`) are partial override bundles;
 * i18next merges them on top of the base-language bundle via its lookup chain
 * `<locale> → <base language> → fallbackLng`.
 */
export const TRANSLATION_LANGUAGES = [
	'cs',
	'da',
	'de',
	'en',
	'en-GB',
	'es',
	'fi',
	'fr',
	'hu',
	'it',
	'nl',
	'nb',
	'pl-PL',
	'pt',
	'pt-PT',
	'ro',
	'sk',
	'sl',
	'sv',
];

/**
 * i18next fallback overrides for locales whose natural lookup chain hits no
 * usable bundle: Norwegian (`no`/`no-NO`) routes to Bokmål, bare `pl` to the
 * Polish regional overrides (there is no `pl` base bundle).
 * Passed to `i18next.init({ fallbackLng })`.
 */
export const FALLBACK_LANGUAGES: Record<string, string[]> = {
	default: ['en'],
	no: [
		'nb',
		'en',
	],
	'no-NO': [
		'nb',
		'en',
	],
	pl: [
		'pl-PL',
		'en',
	],
};

/**
 * Base languages whose translations live in a differently-named bundle.
 */
const BASE_LANGUAGE_ALIASES: Record<string, string> = {
	no: 'nb',
};

/**
 * Resolve the locale bundles that must be available before the app mounts:
 * every bundle on the i18next lookup chain for the active locale — the exact
 * regional overrides (if any), the base-language bundle, and the `en`
 * fallback consulted for missing keys.
 *
 * @param locale Active locale as resolved by `tools/get-locale` (e.g. `nl`, `en-GB`, `de-DE`).
 * @returns Bundle names to load before mount, in i18next lookup order.
 */
export function resolveBootLanguages(locale?: string): string[] {
	const normalizedLocale = (locale || '').toLowerCase();
	const baseInput = normalizedLocale.split('-')[0];
	const baseLanguage = BASE_LANGUAGE_ALIASES[baseInput] ?? baseInput;
	const exactMatch = TRANSLATION_LANGUAGES.find((ln) => ln.toLowerCase() === normalizedLocale);
	const baseMatch = TRANSLATION_LANGUAGES.find((ln) => ln === baseLanguage)
		/*
		 * A base locale without an own bundle (e.g. `pl`) still has regional
		 * overrides (`pl-PL`) — load those rather than nothing; the matching
		 * FALLBACK_LANGUAGES entry makes i18next consult them.
		 */
		?? TRANSLATION_LANGUAGES.find((ln) => ln.split('-')[0] === baseLanguage);

	// Unique, in i18next lookup order: regional override, base bundle, then the `en` fallback.
	return [
		...new Set([
			exactMatch,
			baseMatch,
			'en',
		].filter((ln): ln is string => Boolean(ln))),
	];
}

/**
 * Resolve the locale bundles that can load after mount: everything in
 * `TRANSLATION_LANGUAGES` not already loaded at boot. Keeping these available
 * preserves instant runtime language switching (account settings, native
 * settings dialog) without blocking the initial render on ~36 bundle fetches.
 *
 * @param locale Active locale as resolved by `tools/get-locale`.
 * @returns Bundle names to load in the background, in list order.
 */
export function resolveBackgroundLanguages(locale?: string): string[] {
	const bootLanguages = resolveBootLanguages(locale);

	return TRANSLATION_LANGUAGES.filter((ln) => !bootLanguages.includes(ln));
}
